// SA English value formatting for the shared kit. Money and quantities come from the ONE
// formatter in @enp/domain (number style D-022: "R 12 480.00", "4.5 kg", "−R 99.00"), so a
// change of style happens in one place. Dates and times: "27 Sep", 24-hour "14:05", always in
// the account time zone.

import { formatNumber, formatQty as formatDomainQty } from '@enp/domain/format';
import { cents as toCents, formatMoney as formatDomainMoney } from '@enp/domain/money';
import { DEFAULT_TIME_ZONE } from '@enp/domain/time';

/** The business zone when none is known (SAST); the one constant lives in @enp/domain. */
export { DEFAULT_TIME_ZONE };

/** Integer cents → "R 1 234.50" (negative: "−R 1 234.50"). Throws RangeError on a non-integer. */
export function formatMoney(cents: number): string {
  return formatDomainMoney(toCents(cents));
}

/**
 * Decimal quantity (as a string, never a float) → "1 234.5 kg". Trailing zeros trimmed, at
 * most `maxDecimals` places (rounded half-up, exactly).
 */
export function formatQty(value: string, unit?: string, maxDecimals = 3): string {
  const v = value.trim();
  return unit ? formatDomainQty(v, unit, { maxDecimals }) : formatNumber(v, { maxDecimals });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function zonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-ZA', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
}

export interface DateFormatOptions {
  timeZone?: string;
  /** Reference "now" (for the year cut-off); injected in tests. */
  now?: Date;
}

/** "27 Sep" in the current year, "27 Sep 2025" otherwise. */
export function formatDate(value: Date | string, options: DateFormatOptions = {}): string {
  const tz = options.timeZone ?? DEFAULT_TIME_ZONE;
  const d = zonedParts(new Date(value), tz);
  const now = zonedParts(options.now ?? new Date(), tz);
  const base = `${d.day} ${MONTHS[d.month - 1] ?? ''}`;
  return d.year === now.year ? base : `${base} ${d.year}`;
}

/** 24-hour "14:05". */
export function formatTime(value: Date | string, options: DateFormatOptions = {}): string {
  const d = zonedParts(new Date(value), options.timeZone ?? DEFAULT_TIME_ZONE);
  return `${String(d.hour).padStart(2, '0')}:${String(d.minute).padStart(2, '0')}`;
}

/** "27 Sep, 14:05". */
export function formatDateTime(value: Date | string, options: DateFormatOptions = {}): string {
  return `${formatDate(value, options)}, ${formatTime(value, options)}`;
}
